import {
  EnvironmentAuthInvalidError,
  type EnvironmentId,
  EnvironmentScopeRequiredError,
  type InferenceFailureKind,
  TRANSCRIPTIONS_ROUTE_PATH,
  TranscriptionErrorResponse,
  type TranscriptionFailureReason,
  TranscriptionResponse,
  VOICE_TRANSCRIPTION_MAX_BYTES,
  voiceTranscriptionTimeoutMs,
} from "@t3tools/contracts";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";

import { currentServerToken } from "./environments/primary/serverToken";
import { refreshPrimarySessionState } from "./environments/primary/sessionState";
import { resolvePrimaryEnvironmentHttpUrl } from "./environments/primary/target";
import { readEnvironmentConnection } from "./environments/runtime";

const decodeTranscriptionResponse = Schema.decodeUnknownSync(TranscriptionResponse);
const decodeTranscriptionErrorResponse = Schema.decodeUnknownOption(TranscriptionErrorResponse);
const decodeAuthRefusal = Schema.decodeUnknownOption(
  Schema.Union([EnvironmentAuthInvalidError, EnvironmentScopeRequiredError]),
);

export type VoiceTranscriptionFailure =
  | { readonly kind: "disconnected" }
  | { readonly kind: "network" }
  | { readonly kind: "timeout" }
  | { readonly kind: "tooLarge" }
  | { readonly kind: "voiceInputDisabled" }
  | { readonly kind: "notConfigured" }
  | { readonly kind: "invalidRecording" }
  | { readonly kind: "uploadStalled" }
  | { readonly kind: "unavailable"; readonly inferenceFailure: InferenceFailureKind | null }
  | { readonly kind: "unauthorized"; readonly status: 401 | 403 }
  | { readonly kind: "invalidResponse" }
  | { readonly kind: "empty" }
  | { readonly kind: "interrupted"; readonly phase: "recording" | "transcribing" }
  | { readonly kind: "tooLong" }
  | { readonly kind: "audioUnavailable" }
  | { readonly kind: "unknown"; readonly detail: string };

export class VoiceTranscriptionError extends Error {
  readonly failure: VoiceTranscriptionFailure;

  constructor(failure: VoiceTranscriptionFailure) {
    super(`Voice transcription failed: ${failure.kind}`);
    this.name = "VoiceTranscriptionError";
    this.failure = failure;
  }
}

export function toVoiceTranscriptionFailure(error: unknown): VoiceTranscriptionFailure {
  if (error instanceof VoiceTranscriptionError) return error.failure;
  if (error instanceof DOMException && error.name === "TimeoutError") return { kind: "timeout" };
  if (error instanceof TypeError) return { kind: "network" };
  return { kind: "unknown", detail: error instanceof Error ? error.message : String(error) };
}

const UPLOAD_ALLOWANCE_BASE_MS = 30_000;
const UPLOAD_ALLOWANCE_MS_PER_MEGABYTE = 10_000;

/**
 * The server's own upstream wait plus the time this client needs to upload the
 * recording to its server. Aborting sooner abandons a transcription the server
 * still completes and bills.
 */
export function voiceTranscriptionRequestTimeoutMs(sizeBytes: number): number {
  return (
    voiceTranscriptionTimeoutMs(sizeBytes) +
    UPLOAD_ALLOWANCE_BASE_MS +
    Math.ceil(sizeBytes / (1024 * 1024)) * UPLOAD_ALLOWANCE_MS_PER_MEGABYTE
  );
}

export interface VoiceTranscriptionRequest {
  readonly environmentId: EnvironmentId;
  readonly audio: Blob;
  readonly mimeType: string;
  readonly signal: AbortSignal;
}

export interface VoiceTranscriptionTransport {
  readonly fetch?: typeof fetch;
  readonly timeoutMs?: number;
}

const FAILURE_BY_REASON: Record<
  Exclude<TranscriptionFailureReason, "unavailable">,
  VoiceTranscriptionFailure
> = {
  voice_input_disabled: { kind: "voiceInputDisabled" },
  not_configured: { kind: "notConfigured" },
  too_large: { kind: "tooLarge" },
  invalid_recording: { kind: "invalidRecording" },
  upload_stalled: { kind: "uploadStalled" },
};

function parseJson(body: string): unknown {
  try {
    return JSON.parse(body);
  } catch {
    return undefined;
  }
}

/**
 * The route answers every failure of its own with a {@link TranscriptionErrorResponse};
 * an auth refusal keeps the server's common shape. Anything else — a proxy's
 * HTML page, a body from a newer server this client cannot decode — reads as
 * an outage.
 */
export function readTranscriptionFailure(status: number, body: string): VoiceTranscriptionFailure {
  const json = parseJson(body);
  const routeFailure = decodeTranscriptionErrorResponse(json);
  if (Option.isSome(routeFailure)) {
    const { error, kind } = routeFailure.value;
    return error === "unavailable"
      ? { kind: "unavailable", inferenceFailure: kind ?? null }
      : FAILURE_BY_REASON[error];
  }
  if ((status === 401 || status === 403) && Option.isSome(decodeAuthRefusal(json))) {
    return { kind: "unauthorized", status };
  }
  return { kind: "unavailable", inferenceFailure: null };
}

export async function transcribeVoiceRecording(
  request: VoiceTranscriptionRequest,
  transport: VoiceTranscriptionTransport = {},
): Promise<string> {
  if (request.audio.size > VOICE_TRANSCRIPTION_MAX_BYTES) {
    throw new VoiceTranscriptionError({ kind: "tooLarge" });
  }
  if (!readEnvironmentConnection(request.environmentId)) {
    throw new VoiceTranscriptionError({ kind: "disconnected" });
  }
  // The server that serves this client: loopback under the desktop shell, the
  // page's own origin on a team server.
  const url = resolvePrimaryEnvironmentHttpUrl(TRANSCRIPTIONS_ROUTE_PATH);
  const fetchImpl = transport.fetch ?? fetch;
  const signal = AbortSignal.any([
    request.signal,
    AbortSignal.timeout(
      transport.timeoutMs ?? voiceTranscriptionRequestTimeoutMs(request.audio.size),
    ),
  ]);
  const headers = new Headers({ "Content-Type": request.mimeType });
  const token = currentServerToken();
  if (token !== null) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // A Blob body carries its own Content-Length, which the route checks the
  // received bytes against.
  const response = await fetchImpl(url, {
    method: "POST",
    headers,
    body: request.audio,
    signal,
  });
  const body = await response.text();
  if (!response.ok) {
    const failure = readTranscriptionFailure(response.status, body);
    if (failure.kind === "unauthorized") {
      // The session watcher decides whether this is an expired token or a
      // signed-out account, and sends the user to sign in for the latter.
      refreshPrimarySessionState();
    }
    throw new VoiceTranscriptionError(failure);
  }
  let text: string;
  try {
    text = decodeTranscriptionResponse(JSON.parse(body)).text;
  } catch {
    throw new VoiceTranscriptionError({ kind: "invalidResponse" });
  }
  if (text.trim().length === 0) {
    throw new VoiceTranscriptionError({ kind: "empty" });
  }
  return text;
}
